import { Box } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { IPaginatedListChangeEvent, IPaginatedListRef } from '../../components/PaginatedList/PaginatedList';
import ReportsGridView from '../../components/ReportsComponent/ReportsGridView/ReportsGridView';
import ReportsListView from '../../components/ReportsComponent/ReportsListView/ReportsListView';
import { PageHeader } from '../../components/_mui';
import { MAX_REPORTS_PER_PAGE } from '../../constants/reports';
import { routes, URL_VARS } from '../../constants/routing';
import { INITIAL_PAGE } from '../../constants/table';
import { useHeaderDispatch } from '../../contexts/HeaderInfo';
import { fetchBusinessReasonsRequest } from '../../store/actions/reasons';
import { fetchUsersRequest } from '../../store/actions/users';
import { selectIsAdminTypeUser } from '../../store/selectors/auth';
import { selectBusinessReasons } from '../../store/selectors/reasons';
import { selectIsFinalReport } from '../../store/selectors/reports';
import { selectUsers } from '../../store/selectors/users';
import { IReport } from '../../types/reports';
import { ViewTypesEnum } from '../../types/shell';
import useFilters from './Filters/useFilters';

const ReportsContainer = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { setHeader } = useHeaderDispatch();
  const [currentPageSize, setCurrentPageSize] = useState(MAX_REPORTS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(INITIAL_PAGE);
  const paginatedListRef = useRef<IPaginatedListRef<IReport>>(null);

  const isAdminTypeUser = useSelector(selectIsAdminTypeUser);
  const users = useSelector(selectUsers);
  const reasons = useSelector(selectBusinessReasons);
  const isFinalReport = useSelector(selectIsFinalReport);

  const handleCopyBtnClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  }, []);

  const {
    filteredReports,
    filters,
    reportsCount,
    isDeliveryReportsFetching,
    filtersRef,
    Filters,
    activeView,
    fetchReports,
  } = useFilters({
    currentPageSize,
  });

  const { tab } = filters;

  const handlePaginationChange = useCallback(
    ({ page, pageSize, setPage, setPageSize }: IPaginatedListChangeEvent) => {
      const pageSizeToUse = pageSize || currentPageSize;

      fetchReports({
        page,
        page_size: pageSizeToUse,
      });
      setPage(page);
      setPageSize?.(pageSizeToUse);
      setCurrentPage(page);
      setCurrentPageSize(pageSizeToUse);
    },

    [currentPageSize, fetchReports],
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      setCurrentPageSize(newPageSize);
      setCurrentPage(INITIAL_PAGE);
      fetchReports({
        page: INITIAL_PAGE,
        page_size: newPageSize,
      });
      if (paginatedListRef.current) {
        paginatedListRef.current.resetPageCount();
      }
    },
    [fetchReports],
  );

  const handleCardClick = useCallback(
    (uid: string | undefined) => {
      if (!uid) {
        return;
      }

      history.push(routes.reports.getReportsUrl({ reportId: uid, flowId: URL_VARS.SUMMARY, type: tab }));
    },
    [history, tab],
  );

  // resets the page to 1 when filters change (skip initial render — useFilters handles the first fetch)
  const isFiltersInitialRender = useRef(true);

  useEffect(() => {
    if (isFiltersInitialRender.current) {
      isFiltersInitialRender.current = false;
      return;
    }

    setCurrentPage(INITIAL_PAGE);
    fetchReports({
      page: INITIAL_PAGE,
      page_size: currentPageSize,
    });
    paginatedListRef.current?.resetPageCount();
  }, [filters]);

  useEffect(() => {
    if (isAdminTypeUser && (!users || !users.length)) {
      dispatch(fetchUsersRequest());
    }
  }, [users, dispatch, isAdminTypeUser]);

  useEffect(() => {
    if (!reasons || !reasons.length) {
      dispatch(fetchBusinessReasonsRequest());
    }
  }, [reasons, dispatch]);

  useEffect(() => {
    if (typeof setHeader === 'function') {
      setHeader({ title: 'Delivery reports', action: () => history.push(routes.dashboard) });
    }
  }, [setHeader, history]);

  return (
    <Box sx={{ width: '100%', display: 'flex', flexFlow: 'column' }}>
      <PageHeader>{Filters}</PageHeader>
      {activeView === ViewTypesEnum.List ? (
        <ReportsListView
          onChange={handlePaginationChange}
          totalCount={reportsCount}
          reports={filteredReports}
          isLoading={isDeliveryReportsFetching}
          ref={filtersRef}
          filters={filters}
          page={currentPage}
          pageSize={currentPageSize}
        />
      ) : (
        <ReportsGridView
          totalCount={reportsCount}
          ref={paginatedListRef}
          items={filteredReports || []}
          onChange={handlePaginationChange}
          isFinalReport={isFinalReport}
          isReportsFetching={isDeliveryReportsFetching}
          onCardClick={handleCardClick}
          onCopyBtnClick={handleCopyBtnClick}
          filtersRef={filtersRef}
          activeTab={tab}
          pageSize={currentPageSize}
          onPageSizeChange={handlePageSizeChange}
          page={currentPage}
        />
      )}
    </Box>
  );
};

ReportsContainer.whyDidYouRender = false;

export default ReportsContainer;
